import { Injectable } from '@angular/core';
import {
  CategoriaTecnica,
  IEventoGenerico,
  TipoEventoGenerico,
} from 'modelos/src';
import { IEstadoCelda } from '../../../auxiliares/tabla/tabla.interfaces';
import { escaparHtml } from '../../../auxiliares/html-seguro.util';
import { estadoHtml } from '../../../auxiliares/tabla/celdas';

/** Estados del tratamiento de un evento (pieza `estado`). */
const ESTADOS_EVENTO: Record<string, IEstadoCelda> = {
  'Sin Tratamiento': {
    texto: 'Sin tratamiento',
    tono: 'aviso',
    icono: 'warning',
  },
  Pendiente: { texto: 'Pendiente', tono: 'info', icono: 'pending' },
  'En Atención': { texto: 'En atención', tono: 'info', icono: 'pending' },
  Liberada: { texto: 'Liberada', tono: 'info', icono: 'lock_open' },
  'En Espera': { texto: 'En espera', tono: 'info', icono: 'pause' },
  Finalizada: { texto: 'Finalizada', tono: 'ok', icono: 'task_alt' },
};

/** Estados de un servicio técnico (pieza `estado`). */
const ESTADOS_SERVICIO_TECNICO: Record<string, IEstadoCelda> = {
  Pendiente: { texto: 'Pendiente', tono: 'info', icono: 'pending' },
  Asignado: { texto: 'Asignado', tono: 'info', icono: 'pending' },
  'En Atención': { texto: 'En atención', tono: 'info', icono: 'pending' },
  'Pendiente de Aprobación': {
    texto: 'Pendiente de aprobación',
    tono: 'aviso',
    icono: 'pending',
  },
  Finalizado: { texto: 'Finalizado', tono: 'ok', icono: 'task_alt' },
};

@Injectable({
  providedIn: 'root',
})
export class EventosGenericosHelperService {
  // Entidad con su ícono (docs/ICONOS.md), con las clases de celda de app-tabla
  private static iconHtml(icon: string, text: string): string {
    return `<div class="tabla-linea"><span class="material-symbols-outlined">${icon}</span><span>${escaparHtml(text)}</span></div>`;
  }

  /**
   * Celda de categoría: badge del color de la categoría con el nombre adentro
   * (neutro si el color falta o no es válido). Vacío si no hay categoría.
   */
  public categoriaHtml(evento: IEventoGenerico): string {
    const categoria = evento.categoria || evento.tipoEvento;
    if (!categoria) return '';
    return estadoHtml({
      texto: categoria,
      tono: 'neutro',
      color: evento.valores?.color,
    });
  }

  /**
   * Estado del tratamiento de un evento como pieza `estado` (ícono de
   * docs/ICONOS.md §4 + nombre real con mayúscula inicial + tono).
   */
  public static estadoEvento(estado?: string): IEstadoCelda | undefined {
    if (!estado) return undefined;
    return ESTADOS_EVENTO[estado] ?? { texto: estado, tono: 'neutro' };
  }

  /** Estado de un servicio técnico como pieza `estado`. */
  public static estadoServicioTecnico(
    estado?: string,
  ): IEstadoCelda | undefined {
    if (!estado) return undefined;
    return (
      ESTADOS_SERVICIO_TECNICO[estado] ?? { texto: estado, tono: 'neutro' }
    );
  }

  public obtenerEntidad(evento: IEventoGenerico): string {
    if (evento.tipoEvento === 'Evento Activo') {
      return evento.activo?.identificacion
        ? EventosGenericosHelperService.iconHtml(
            'assistant_navigation',
            evento.activo.identificacion,
          )
        : '';
    }
    if (evento.tipoEvento === 'Evento Alarma') {
      return evento.alarma?.nombre
        ? EventosGenericosHelperService.iconHtml(
            'doorbell',
            evento.alarma.nombre,
          )
        : '';
    }
    if (evento.tipoEvento === 'Evento Vehiculo') {
      return evento.activo?.identificacion
        ? EventosGenericosHelperService.iconHtml(
            'directions_car',
            evento.activo.identificacion,
          )
        : '';
    }
    if (evento.tipoEvento === 'Evento Colectivo') {
      return evento.activo?.identificacion
        ? EventosGenericosHelperService.iconHtml(
            'directions_bus',
            evento.activo.identificacion,
          )
        : '';
    }
    if (evento.tipoEvento === 'Evento Tracker') {
      return evento.tracker?.identificacion
        ? EventosGenericosHelperService.iconHtml(
            'gps_fixed',
            evento.tracker.identificacion,
          )
        : '';
    }
    if (evento.tipoEvento === 'Evento Técnico Tracker') {
      if (evento.tracker?.nombre || evento.tracker?.identificacion) {
        return EventosGenericosHelperService.iconHtml(
          'gps_fixed',
          String(evento.tracker?.nombre || evento.tracker?.identificacion),
        );
      }
      return '';
    }
    if (evento.tipoEvento === 'Evento Técnico Vehículo') {
      return evento.activo?.identificacion
        ? EventosGenericosHelperService.iconHtml(
            'directions_car',
            evento.activo.identificacion,
          )
        : '';
    }
    if (evento.tipoEvento === 'Evento Técnico Alarma') {
      return evento.alarma?.nombre
        ? EventosGenericosHelperService.iconHtml(
            'doorbell',
            evento.alarma.nombre,
          )
        : '';
    }
    if (evento.tipoEvento === 'Evento Luminaria') {
      return evento.luminaria?.identificacion
        ? EventosGenericosHelperService.iconHtml(
            'lightbulb',
            evento.luminaria.identificacion,
          )
        : '';
    }
    if (evento.tipoEvento === 'Evento Técnico Luminaria') {
      return evento.luminaria?.identificacion
        ? EventosGenericosHelperService.iconHtml(
            'lightbulb',
            evento.luminaria.identificacion,
          )
        : '';
    }
    if (evento.tipoEvento === 'Evento BotonBLE') {
      return evento.botonBluetooth?.mac
        ? EventosGenericosHelperService.iconHtml(
            'touch_app',
            evento.botonBluetooth.mac,
          )
        : '';
    }
    if (evento.tipoEvento === 'Evento Sirena') {
      return evento.sirena?.chipId
        ? EventosGenericosHelperService.iconHtml(
            'campaign',
            evento.sirena.chipId,
          )
        : '';
    }
    if (evento.tipoEvento === 'Evento Técnico Sirena') {
      return evento.sirena?.chipId
        ? EventosGenericosHelperService.iconHtml(
            'campaign',
            evento.sirena.chipId,
          )
        : '';
    }
    if (evento.tipoEvento === 'Evento Gateway') {
      return evento.gateway?.nombre
        ? EventosGenericosHelperService.iconHtml(
            'podcasts',
            evento.gateway.nombre,
          )
        : '';
    }
    if (evento.tipoEvento === 'Evento Técnico Gateway') {
      return evento.gateway?.nombre
        ? EventosGenericosHelperService.iconHtml(
            'podcasts',
            evento.gateway.nombre,
          )
        : '';
    }
    return '';
  }

  public obtenerCategoria(evento: IEventoGenerico): string {
    // Por ahora el único diferente es Sirena que lo saca de valores.efecto
    // El resto es categoria
    if (evento.tipoEvento === 'Evento Sirena') {
      return evento.valores?.efecto || 'Sin categoría';
    }
    return evento.categoria || 'Sin categoría';
  }

  public obtenerCategoriaTecnica(
    tipoEvento: TipoEventoGenerico,
  ): CategoriaTecnica {
    if (tipoEvento === 'Evento Alarma') {
      return 'Alarma';
    }
    if (
      tipoEvento === 'Evento Tracker' ||
      tipoEvento === 'Evento Vehiculo' ||
      tipoEvento === 'Evento Colectivo'
    ) {
      return 'Tracker';
    }
    if (tipoEvento === 'Evento Luminaria') {
      return 'Luminaria';
    }
    if (tipoEvento === 'Evento Sirena') {
      return 'Sirena';
    }
    if (tipoEvento === 'Evento Gateway') {
      return 'Gateway';
    }
    return 'Alarma';
  }
}
